import React from "react";
import { IActiviityProps, IActicityRootProps } from "./Activity.types";
import { StandardCard } from "../Cards";
import { Box, Tab, Tabs, IconButton } from "@mui/material";
import { a11yProps } from "../Tabs/utils";
import { TabPanel } from "../Tabs/TabPanel";
import { Activity } from "./Activity";
import { ActivityCommentsLogs } from "./CommentsLogs";
import { ActivityHistoryLogs } from "./ActivityHistory";
import { ActivityEmailConversationLogs } from "./ActivityEmailConversationLogs";
import { ActivitySMSLogs } from "./ActivitySMSLogs";
import { ActivityWhatsAppLogs } from "../../views/Chatbot/ChatCenter/ActivityWhatsAppLog";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HistoryIcon from "@mui/icons-material/History";
import { Tour, Close } from "@mui/icons-material";
import { ActivityWhatsAppGroupLogs } from "./ActivityWhatsAppGroupLogs";
import { GuestVisitHistory } from "../../views/customers/components/GuestVisitHistory/GuestVisitHistory";

export const ActivityRoot: React.FC<IActicityRootProps> = (props) => {
  const { activity, moduleInformation, onClose } = props;
  const isSupportTicket = moduleInformation?.moduleName === "SUPPORT_TICKET";
  const [selectedTab, setSelectedTab] = React.useState(isSupportTicket ? 1 : 0);


  const handleTabChange = (event: React.SyntheticEvent, value: any) => {
    setSelectedTab(value);
  };
  const chatContainerRef = React.useRef<HTMLDivElement | null>(null);

  const scrollToBottom = React.useCallback((smooth = false) => {
    const el = chatContainerRef.current;

    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
  }, []);

  // Scroll when WhatsApp tab opens
  React.useEffect(() => {
    if (selectedTab === 4) {
      setTimeout(() => scrollToBottom(false), 100);
    }
  }, [selectedTab, scrollToBottom]);

  // Scroll when new messages render
  React.useEffect(() => {
    const el = chatContainerRef.current;
    if (!el) return;

    const observer = new MutationObserver(() => {
      scrollToBottom(false);
    });

    observer.observe(el, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [scrollToBottom]);

  return (
    <StandardCard
      sx={{
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        pt: 1.5,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 1,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            flex: 1,
            minHeight: 38,
            "& .MuiTab-root": {
              minHeight: 38,
              minWidth: "auto",
              py: 0.5,
              px: 2,
              fontSize: "0.85rem",
              fontWeight: 600,
              textTransform: "none",
              color: "text.secondary",
            },
            "& .Mui-selected": { color: "primary.main" },
            "& .MuiTabs-indicator": { height: 2, borderRadius: 1 },
          }}
        >
          {!isSupportTicket && (
            <Tab
              icon={<HistoryIcon fontSize="small" />}
              iconPosition="start"
              label="History"
              {...a11yProps(0)}
            />
          )}

          {activity && activity.visitHistory && (
            <Tab
              icon={<Tour fontSize="small" />}
              iconPosition="start"
              label="Guest"
              value={8}
              {...a11yProps(8)}
            />
          )}

          {activity && activity.comments && (
            <Tab label="Comments" value={1} {...a11yProps(1)} />
          )}

          {activity && activity.whatsAppChat && (
            <Tab
              icon={<WhatsAppIcon fontSize="small" />}
              iconPosition="start"
              label="WhatsApp"
              value={4}
              {...a11yProps(4)}
            />
          )}

          {activity && activity.invoice && (
            <Tab label="Invoicing" {...a11yProps(6)} tabIndex={6} value={6} />
          )}
        </Tabs>

        {onClose && (
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ ml: 1, flexShrink: 0 }}
            aria-label="Close activity panel"
          >
            <Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Scrollable content */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* {activity && (
        <TabPanel value={selectedTab} index={0}>
          <Activity
            moduleInformation={moduleInformation}
            email={activity.email}
            task={activity.task}
            phoneWhatsAppSMS={activity.phoneWhatsAppSMS}
          />
        </TabPanel>
      )} */}
        {!isSupportTicket && (
          <TabPanel
            value={selectedTab}
            index={0}
            style={{
              display: selectedTab === 0 ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <ActivityHistoryLogs moduleInformation={moduleInformation} />
          </TabPanel>
        )}

        <TabPanel
          value={selectedTab}
          index={1}
          style={{
            display: selectedTab === 1 ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            height: "100%",
          }}
        >
          <ActivityCommentsLogs moduleInformation={moduleInformation} />
        </TabPanel>
        <TabPanel
          value={selectedTab}
          index={2}
          style={{
            display: selectedTab === 2 ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            height: "100%",
          }}
        >
          <ActivityEmailConversationLogs
            moduleInformation={moduleInformation}
          />
        </TabPanel>
        <TabPanel
          value={selectedTab}
          index={3}
          style={{
            display: selectedTab === 3 ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            height: "100%",
          }}
        >
          <ActivitySMSLogs moduleInformation={moduleInformation} />
        </TabPanel>
        {activity && activity.whatsAppChat && (
          <TabPanel
            value={selectedTab}
            index={4}
            style={{
              display: selectedTab === 4 ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              height: "100%",
            }}
          >
            <ActivityWhatsAppLogs
              moduleInformation={moduleInformation}
              phone={activity.whatsAppChat.phone}
              customerName={activity.whatsAppChat.customerName}
              isFromActivityRoot
            />
          </TabPanel>
        )}
        <TabPanel
          value={selectedTab}
          index={5}
          style={{
            display: selectedTab === 5 ? "flex" : "none",
            flexDirection: "column",
            flex: 1,
            height: "100%",
          }}
        >
          {/* <ActivityInvoiceLogs moduleInformation={moduleInformation} /> */}
        </TabPanel>
        {activity && activity.WhatsAppGroupChat && (
          <TabPanel
            value={selectedTab}
            index={7}
            style={{
              display: selectedTab === 7 ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              height: "100%",
            }}
          >
            <ActivityWhatsAppGroupLogs
              moduleInformation={moduleInformation}
              phone={activity.WhatsAppGroupChat.phone}
              customerName={activity.WhatsAppGroupChat.customerName}
            />
          </TabPanel>
        )}

        {activity && activity.visitHistory && (
          <TabPanel
            value={selectedTab}
            index={8}
            style={{
              display: selectedTab === 8 ? "flex" : "none",
              flexDirection: "column",
              flex: 1,
              height: "100%",
            }}
          >
            <GuestVisitHistory
              moduleInformation={moduleInformation}
              isVisitHistoryLogs
              customerFactUuid={activity.visitHistory.customerFactUuid}
            />
          </TabPanel>
        )}
      </Box>
    </StandardCard>
  );
};
